import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Alert, Stack, LinearProgress, Tooltip, List, ListItem, ListItemText,
} from '@mui/material';
import { Add, Edit, Delete, Build, Download } from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const emptyDevice = {
  name: '',
  station_id: '',
  region_id: '',
  contribution_user_id: '',
  assigned_user_id: '',
  splits_mode: 'cloud',
  is_enabled: true,
  notes: '',
};

const statusColor = {
  queued: '#888',
  building: '#f59e0b',
  signing: '#f59e0b',
  completed: '#22c55e',
  failed: '#ef4444',
};

/**
 * Settings → Studio Output: devices, per-customer builds, and setup guidance.
 * Playout itself runs in the Studio Mode desktop app.
 */
export default function StudioModeSettings() {
  const { isAdmin } = useAuth();
  const canManage = isAdmin();
  const [devices, setDevices] = useState([]);
  const [stations, setStations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [users, setUsers] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDevice);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [studioOutputEnabled, setStudioOutputEnabled] = useState(true);

  const load = useCallback(async () => {
    try {
      const opts = { withCredentials: true };
      const adminRequests = canManage
        ? [
          axios.get(`${API_URL}/api/stations`, opts),
          axios.get(`${API_URL}/api/webrtc-contributors`, opts).catch((error) => {
            if (error.response?.status === 403) return { data: [] };
            throw error;
          }),
          axios.get(`${API_URL}/api/users`, opts),
          axios.get(`${API_URL}/api/system-settings`, opts),
        ]
        : [
          Promise.resolve({ data: [] }),
          Promise.resolve({ data: [] }),
          Promise.resolve({ data: [] }),
          Promise.resolve({ data: {} }),
        ];
      const [devicesRes, buildsRes, stationsRes, contributorsRes, usersRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/studio-mode/devices`, opts),
        axios.get(`${API_URL}/api/studio-mode/builds`, opts),
        ...adminRequests,
      ]);
      setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
      setBuilds(Array.isArray(buildsRes.data) ? buildsRes.data : []);
      setStations(Array.isArray(stationsRes.data) ? stationsRes.data : []);
      setContributors(contributorsRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      const d = settingsRes.data || {};
      setStudioOutputEnabled(
        d.system_studio_output_enabled !== false && d.system_studio_output_enabled !== 'false'
      );
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  // Live build progress
  useEffect(() => {
    if (!canManage) return undefined;
    const socket = io(API_URL, { transports: ['websocket'] });
    socket.on('studio-build:progress', (payload) => {
      setProgress(payload);
      if (payload.status === 'completed' || payload.status === 'failed') load();
    });
    return () => socket.disconnect();
  }, [canManage, load]);

  const latestBuildByDevice = useMemo(() => {
    const latest = {};
    for (const build of builds) {
      if (build.status !== 'completed') continue;
      const current = latest[build.studio_device_id];
      if (!current || new Date(build.created_at) > new Date(current.created_at)) {
        latest[build.studio_device_id] = build;
      }
    }
    return latest;
  }, [builds]);

  // Regions for the selected station in the dialog
  useEffect(() => {
    if (!form.station_id) { setRegions([]); return; }
    axios.get(`${API_URL}/api/regions?station_id=${form.station_id}`, { withCredentials: true })
      .then((r) => setRegions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRegions([]));
  }, [form.station_id]);

  const openDialog = (device = null) => {
    setEditing(device);
    setForm(device ? {
      name: device.name,
      station_id: device.station_id,
      region_id: device.region_id || '',
      contribution_user_id: device.contribution_user_id,
      assigned_user_id: device.assigned_user_id || '',
      splits_mode: device.splits_mode,
      is_enabled: device.is_enabled,
      notes: device.notes || '',
    } : emptyDevice);
    setDialogOpen(true);
  };

  const save = async () => {
    setError(null);
    try {
      const payload = {
        ...form,
        region_id: form.region_id || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/studio-mode/devices/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/studio-mode/devices`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const remove = async (device) => {
    if (!window.confirm(`Delete studio device "${device.name}"?`)) return;
    await axios.delete(`${API_URL}/api/studio-mode/devices/${device.id}`, { withCredentials: true });
    load();
  };

  const queueBuild = async (device) => {
    setError(null);
    try {
      await axios.post(`${API_URL}/api/studio-mode/builds`, { studio_device_id: device.id }, { withCredentials: true });
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const downloadBuild = async (buildId) => {
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/studio-mode/builds/${buildId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `RadioSuite-Studio-build${buildId}.exe`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Download failed');
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {!studioOutputEnabled && (
        <Alert severity="info">
          Studio Output is disabled system-wide. Enable it under Station and App Configuration → App Features.
        </Alert>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>Studio Devices</Typography>
              <Typography variant="body2" color="text.secondary">
                Each device locks a Studio Mode install to a station, contribution account and split behaviour.
                The configuration is compiled into that customer's executable. Download completed builds below,
                then sign in on the studio PC with a RadioSuite account that has Studio Output access.
              </Typography>
            </Box>
            {canManage && (
              <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>Add Device</Button>
            )}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Station / Region</TableCell>
                <TableCell>Contribution Account</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Splits</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">{canManage ? 'Actions' : 'Installer'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>{device.name}</TableCell>
                  <TableCell>
                    {device.station_name}{device.region_name ? ` / ${device.region_name}` : ''}
                  </TableCell>
                  <TableCell>{device.contribution_username}</TableCell>
                  <TableCell>{device.assigned_display_name || device.assigned_username || 'Admin only'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={device.splits_mode === 'cloud' ? 'Cloud (multi-station)' : 'Local'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={device.is_enabled ? 'Enabled' : 'Disabled'}
                      sx={{ bgcolor: device.is_enabled ? 'rgba(34,197,94,0.2)' : undefined }} />
                  </TableCell>
                  <TableCell align="right">
                    {canManage ? (
                      <>
                        <Tooltip title="Build executable for this device">
                          <IconButton size="small" onClick={() => queueBuild(device)}><Build fontSize="small" /></IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => openDialog(device)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => remove(device)}><Delete fontSize="small" /></IconButton>
                      </>
                    ) : latestBuildByDevice[device.id] ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => downloadBuild(latestBuildByDevice[device.id].id)}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No build yet</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {devices.length === 0 && (
                <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No studio devices yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>Setup</Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="1. Download and install"
                secondary="Build an installer for the studio device. The station, cloud address, and contribution account are written into that build. Download it and install it on the studio PC."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="2. Sign in with your RadioSuite account"
                secondary="Use the same username and password as the web app. MFA must be set up in the web app first if your account requires it."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="3. Set the contribution password"
                secondary="In Studio Mode desktop Settings, enter the WebRTC contribution password for the account bound to this device."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="4. Live Assist hours"
                secondary="Hours marked Live Assist (Studio) in Clocks, with a Remote Contribution item for this device, will sync and play out in Studio Mode."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>Builds</Typography>
          {progress && (progress.status === 'building' || progress.status === 'signing') && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Build #{progress.buildId}: {progress.message}
              </Typography>
              <LinearProgress sx={{ mt: 0.5 }} />
            </Box>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {builds.map((build) => (
                <TableRow key={build.id} hover>
                  <TableCell>{build.id}</TableCell>
                  <TableCell>{build.device_name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={build.status.toUpperCase()}
                      sx={{ bgcolor: `${statusColor[build.status]}33`, color: statusColor[build.status] }} />
                    {build.error && (
                      <Tooltip title={build.error}><Typography component="span" variant="caption" sx={{ ml: 1, color: '#ef4444' }}>details</Typography></Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{build.version || '—'}</TableCell>
                  <TableCell>{build.file_size ? `${Math.round(build.file_size / 1024 / 1024)} MB` : '—'}</TableCell>
                  <TableCell>{new Date(build.created_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {build.status === 'completed' && build.artifact_path ? (
                      <Tooltip title="Download installer">
                        <IconButton
                          size="small"
                          onClick={() => downloadBuild(build.id)}
                        >
                          <Download fontSize="small" sx={{ color: '#22c55e' }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={build.status === 'failed' ? 'Build failed — no installer' : 'Available when the build completes'}>
                        <span>
                          <IconButton size="small" disabled>
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {builds.length === 0 && (
                <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No builds yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {canManage && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editing ? 'Edit Studio Device' : 'Add Studio Device'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
              <FormControl fullWidth>
                <InputLabel>Station</InputLabel>
                <Select label="Station" value={form.station_id} onChange={(e) => setForm({ ...form, station_id: e.target.value, region_id: '' })}>
                  {stations.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              {regions.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Region (optional)</InputLabel>
                  <Select label="Region (optional)" value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })}>
                    <MenuItem value="">Master (no region)</MenuItem>
                    {regions.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth>
                <InputLabel>Contribution Account</InputLabel>
                <Select label="Contribution Account" value={form.contribution_user_id} onChange={(e) => setForm({ ...form, contribution_user_id: e.target.value })}>
                  {contributors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.username}{c.display_name ? ` (${c.display_name})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Assigned Operator</InputLabel>
                <Select label="Assigned Operator" value={form.assigned_user_id} onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}>
                  <MenuItem value="">Unassigned (admins only)</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}{user.first_name || user.last_name ? ` (${[user.first_name, user.last_name].filter(Boolean).join(' ')})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Ad / ID Splits</InputLabel>
                <Select label="Ad / ID Splits" value={form.splits_mode} onChange={(e) => setForm({ ...form, splits_mode: e.target.value })}>
                  <MenuItem value="cloud">Fire via cloud (required for multi-station / regional)</MenuItem>
                  <MenuItem value="local">Fire locally in the studio (single station)</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!form.name || !form.station_id || !form.contribution_user_id}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
